import moment from 'moment';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import type { JobDetailsPrintOptions } from 'src/components/job-details/information/print/JobDetailsPrint';
import { GenericMessages } from 'src/intl/GenericMessages';
import { JobMessages } from 'src/intl/JobMessages';
import type { JobLog } from 'src/models/jobLog';
import { selectAuthUserInfo } from 'src/redux/auth/selectors';
import { useSocketGet } from 'src/utils/socket';
import { SocketContextPortal } from 'src/utils/socket/context/portalSocketContext';
import { Range, RangeSet } from '../../components/visualizations/utils';
import { powerStatus_t } from 'foreman-slink';

/**
 * Resolves the relevant information for the user who created the log
 *
 * If the current user belongs to the same org as the log, we can show the creator's name. But
 * if the current user is accessing the log via share, we dont want to leak the employee's name,
 * so we just display the owning org's name
 * @param logInfo - the log
 */
export function useLogCreatedInfo(logInfo: JobLog | undefined): string {
  const { formatMessage } = useIntl();
  const user = useSelector(selectAuthUserInfo);
  const shareRecord = useMemo(
    () => logInfo?.permissions?.find(p => p.principal === user?.org),
    [logInfo?.permissions, user?.org],
  );

  // if shared, we dont need to bother trying to look up the created by user (in fact permissions will probably reject it)
  const createdById = shareRecord ? undefined : logInfo?.createdBy;
  const createdByOrg = shareRecord ? logInfo?.org : undefined;
  // need to use useSocketGet because it caches the data for us, and annotations might need the cached data
  const { item: createdBy } = useSocketGet(SocketContextPortal, 'users', createdById);
  const { item: org } = useSocketGet(SocketContextPortal, 'organizations', createdByOrg);

  if (!logInfo) {
    return '';
  }

  if (shareRecord) {
    const date = moment(new Date(logInfo.createdAt)).format('MMM D, YYYY');
    return formatMessage(JobMessages.jobInfoSubHeading, {
      userName: org?.orgName ?? formatMessage(GenericMessages.unknownPlaceholder),
      date,
    });
  }

  const userName = !createdBy ? 'unknown' : `${createdBy.given_name} ${createdBy.family_name}`;

  const date = moment(new Date(logInfo.createdAt)).format('MMM D, YYYY');
  return formatMessage(JobMessages.jobInfoSubHeading, {
    userName: userName,
    date,
  });
}

export const getPrintQueryParams = (options: JobDetailsPrintOptions): URLSearchParams => {
  const params = new URLSearchParams();
  if (!options) return params;

  if (options.annotations) params.set('annotations', '');
  if (options.idle) params.set('idle', '');
  if (options.pieCharts) params.set('pieCharts', '');
  if (options.pressure) params.set('pressure', '');
  if (options.timestamps) params.set('timestamps', '');
  if (options.tubesCleaned) params.set('tubesCleaned', '');
  if (options.tubesCompletion) params.set('tubesCompletion', '');
  if (options.tubesAutostrokes) params.set('tubesAutostrokes', '');
  if (options.tubesDepthCleaned) params.set('tubesDepthCleaned', '');
  if (options.tubesLanceSpeed) params.set('tubesLanceSpeed', '');
  if (options.tubesTime) params.set('tubesTime', '');

  return params;
};

/**
 * Given power on/off events and the final msElapsed from a log file, get the RangeSet of all times the controller was off.
 *
 * @param powerEvents
 * @param msElapsed
 */
export const getControllerOffRanges = (powerEvents: { status: number; msElapsed: number }[], msElapsed: number) => {
  const set = new RangeSet(0, Math.max(1, msElapsed));
  try {
    let lastOffTime = -1;
    powerEvents.forEach(event => {
      // each time we get an "off" event, save the time
      if (
        event.status === powerStatus_t.buttonOff ||
        event.status === powerStatus_t.timeoutOff ||
        event.status === powerStatus_t.menuOff ||
        event.status === powerStatus_t.assumedOff
      ) {
        lastOffTime = event.msElapsed;

        // each time we get an "on" event, record the time between the last "off" event and the "on" time in the RangeSet
      } else if (event.status === powerStatus_t.on && lastOffTime !== -1) {
        set.addRange(new Range(lastOffTime, event.msElapsed));
        lastOffTime = -1;
      }
    });

    // technically we shouldn't have a case where we have an unmatched off time and there is ms elapsed past that
    if (lastOffTime !== -1 && lastOffTime !== msElapsed) {
      set.addRange(new Range(lastOffTime, msElapsed));
    }
  } catch (err) {
    console.warn('Error parsing power event range');
    console.warn(err);
  }
  return set;
};
